import { LeftRight } from "@/components/LeftRight.tsx";
import { type ConfiguredWSelectProps, WSelect } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { ResourceIcon } from "@/components/icon/ResourceIcon.tsx";
import { type VirtualMachineLoc, vmQueryAtom } from "@/modules/compute/vmQuery.ts";
import { vpcQueryAtom } from "@/modules/network/vpc/vpcApi.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";

export interface TargetServersSelectParams {
    location: string;
    networkUuid: string;
    usedResourceUuids: string[];
}

export interface TargetServersSelectProps
    extends ConfiguredWSelectProps<VirtualMachineLoc, string>, TargetServersSelectParams {}

//#region Icons

function ShortInfo({ item }: { item: VirtualMachineLoc }) {
    return <span>{item.private_ipv4}</span>;
}

//#endregion

function TargetServersSelectCustom({
    networkUuid,
    items,
    location,
    usedResourceUuids = [],
    ...props
}: TargetServersSelectProps) {
    const { onChange } = props;
    const vpcs = useSuspenseQueryAtom(vpcQueryAtom(location));

    // TODO: should networkUuid be required?
    const vpc = networkUuid ? vpcs.get(networkUuid) : null;
    items = items.filter((item) => vpc?.vm_uuids.includes(item.uuid) && !usedResourceUuids.includes(item.uuid));

    return (
        <WSelect
            className="min-w-20em"
            aria-label="Select target server"
            items={items}
            getKey={(item) => item.uuid}
            getTextValue={(item) => item.name}
            searchItems
            onChange={onChange}
            {...props}
        >
            {(item) => (
                <LeftRight icon={<ResourceIcon item={item} />} title={item.name} right={<ShortInfo item={item} />} />
            )}
        </WSelect>
    );
}

export type TargetServersSelectLoaderProps = Omit<TargetServersSelectProps, "items">;

function TargetServersSelectLoader(props: TargetServersSelectLoaderProps) {
    const vms = useSuspenseQueryAtom(vmQueryAtom(props.location));

    return <TargetServersSelectCustom items={[...vms.values()]} {...props} />;
}

export function TargetServersSelect(props: TargetServersSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <TargetServersSelectCustom {...props} {...renderProps} />}>
            <TargetServersSelectLoader {...props} />
        </WSelectWrapper>
    );
}
